import React, { useEffect, lazy, createElement } from 'react';
import { CountdownTimer } from './CountdownTimer';
import { SocialProofPopup } from './SocialProofPopup';
import { CTAButton } from './CTAButton';
import { UpsellSection } from './UpsellSection';
import { CheckCircle2, Lock, ShieldCheck, Smartphone, Monitor, Tablet } from 'lucide-react';
const PURCHASE_LINK = 'https://fegion.shop/cart/46152988197003:1';
export function SalesPage() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  // Preload hero JPG image for faster LCP
  useEffect(() => {
    const preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'image';
    preload.href = 'https://cdn.magicpatterns.com/uploads/5VNPcLs7gpffmWSFz5Zfha/ChatGPT_Image_9_mar_2026,_00_26_02.jpg';
    preload.fetchPriority = 'high';
    document.head.appendChild(preload);
    return () => {
      document.head.removeChild(preload);
    };
  }, []);
  // Add meta description for SEO
  useEffect(() => {
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = 'Descubre Las 50 Pomadas Medicinales que usaban antes de las farmacias. Aprende a preparar pomadas naturales en casa con plantas medicinales. Incluye 2 bonos gratis.';
  }, []);
  // Defer Meta Pixel loading - non-blocking
  useEffect(() => {
    const loadPixel = () => {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '904978025482552');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
      const noscript = document.createElement('noscript');
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = 'https://www.facebook.com/tr?id=904978025482552&ev=PageView&noscript=1';
      noscript.appendChild(img);
      document.body.appendChild(noscript);
      return {
        script,
        noscript
      };
    };
    let cleanup: {
      script: HTMLScriptElement;
      noscript: HTMLElement;
    } | null = null;
    // Defer pixel loading until browser is idle or after 3 seconds
    if ('requestIdleCallback' in window) {
      const id = (window as any).requestIdleCallback(() => {
        cleanup = loadPixel();
      }, {
        timeout: 3000
      });
      return () => {
        ;
        (window as any).cancelIdleCallback(id);
        if (cleanup) {
          document.head.removeChild(cleanup.script);
          document.body.removeChild(cleanup.noscript);
        }
      };
    } else {
      const timer = setTimeout(() => {
        cleanup = loadPixel();
      }, 2000);
      return () => {
        clearTimeout(timer);
        if (cleanup) {
          document.head.removeChild(cleanup.script);
          document.body.removeChild(cleanup.noscript);
        }
      };
    }
  }, []);
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-green-200" data-id="element-9">
      <CountdownTimer data-id="element-10" />
      <SocialProofPopup data-id="element-11" />
      <main data-id="element-12">
        {/* SECTION 1 - HERO - Compact layout: image visible but text is LCP */}
        <section className="pt-8 pb-12 px-4 max-w-5xl mx-auto" data-id="element-13">
          <div className="inline-block bg-green-100 border border-green-200 rounded-full px-6 py-2 mb-6 mx-auto block text-center" data-id="element-14">
            <p className="text-green-800 font-semibold text-sm md:text-base" data-id="element-15">
              🌿 Para quienes buscan recuperar la sabiduría natural que nuestros
              abuelos usaban para curar dolores, inflamaciones y problemas de la
              piel.
            </p>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight mb-4 uppercase text-center" data-id="element-16">
            <span className="block" data-id="element-17">🌿 LAS 50 POMADAS</span>
            <span className="block text-green-700" data-id="element-18">MEDICINALES</span>
          </h1>

          <h2 className="text-2xl md:text-3xl font-bold text-gray-600 mb-8 text-center" data-id="element-19">
            Que Usaban Antes de las Farmacias
          </h2>

          {/* Hero mockup - JPG image (lightweight), displayed large */}
          <a href="#pack-completo" className="block cursor-pointer hover:opacity-95 transition-opacity mb-10 text-center" data-id="element-20">
            <img src="https://cdn.magicpatterns.com/uploads/5VNPcLs7gpffmWSFz5Zfha/ChatGPT_Image_9_mar_2026,_00_26_02.jpg" alt="Las 50 Pomadas Medicinales - Libro con recetas de pomadas naturales" className="w-full h-auto max-w-xl mx-auto rounded-xl shadow-2xl" width={576} height={576} fetchPriority="high" decoding="async" sizes="(max-width: 576px) 100vw, 576px" data-id="element-21" />
          </a>

          <div className="max-w-3xl mx-auto text-lg md:text-xl text-gray-700 space-y-4 mb-8 font-medium text-center" data-id="element-22">
            <p data-id="element-23">
              Durante generaciones, antes de que existieran las farmacias
              modernas, las personas confiaban en el poder de la naturaleza.
            </p>
            <p className="font-bold text-green-700 text-xl md:text-2xl" data-id="element-24">
              Tenían algo muy poderoso… plantas medicinales, aceites naturales y
              remedios tradicionales.
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-6 font-bold text-green-700 text-lg text-center" data-id="element-25">
            <span data-id="element-26">🌿 Plantas medicinales</span>
            <span data-id="element-27">🌿 Aceites naturales</span>
            <span data-id="element-28">🌿 Remedios tradicionales</span>
          </div>

          <CTAButton text="👉 SÍ, QUIERO RECUPERAR ESTE CONOCIMIENTO AHORA" href="#pack-completo" data-id="element-29" />

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mt-6 mb-8 font-semibold text-gray-700 text-center" data-id="element-30">
            <span className="flex items-center gap-2" data-id="element-31">
              <CheckCircle2 className="text-green-600 w-5 h-5" data-id="element-32" /> Acceso
              inmediato
            </span>
            <span className="flex items-center gap-2" data-id="element-33">
              <CheckCircle2 className="text-green-600 w-5 h-5" data-id="element-34" /> Formato
              digital
            </span>
            <span className="flex items-center gap-2" data-id="element-35">
              <CheckCircle2 className="text-green-600 w-5 h-5" data-id="element-36" /> Recetas paso a
              paso
            </span>
          </div>

          <div className="max-w-3xl mx-auto text-lg md:text-xl text-gray-700 mb-4 font-medium text-center" data-id="element-37">
            <p className="mb-4" data-id="element-38">
              Con esos simples ingredientes preparaban pomadas curativas que
              ayudaban a aliviar:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-left max-w-2xl mx-auto" data-id="element-39">
              <span className="flex items-center gap-2" data-id="element-40">
                <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-41" />{' '}
                Dolores musculares
              </span>
              <span className="flex items-center gap-2" data-id="element-42">
                <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-43" />{' '}
                Inflamaciones
              </span>
              <span className="flex items-center gap-2" data-id="element-44">
                <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-45" />{' '}
                Golpes
              </span>
              <span className="flex items-center gap-2" data-id="element-46">
                <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-47" />{' '}
                Irritaciones de la piel
              </span>
              <span className="flex items-center gap-2" data-id="element-48">
                <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-49" />{' '}
                Picaduras
              </span>
              <span className="flex items-center gap-2" data-id="element-50">
                <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-51" />{' '}
                Resfriados
              </span>
            </div>
          </div>
        </section>

        {/* SECTION 2 - THE LOST KNOWLEDGE */}
        <section className="bg-gray-50 py-16 px-4 border-y border-gray-200" data-id="element-52">
          <div className="max-w-4xl mx-auto" data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-10" data-id="element-54">
              📘 Hoy Puedes Recuperar Ese Conocimiento Perdido
            </h2>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 mb-8" data-id="element-55">
              <p className="text-lg md:text-xl text-gray-700 font-medium mb-6" data-id="element-56">
                Hemos recopilado ese saber tradicional en una guía práctica
                llamada:
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-green-700 text-center mb-6" data-id="element-57">
                Las 50 Pomadas Medicinales Que Usaban Antes de las Farmacias
              </h3>
              <p className="text-lg text-gray-700 font-medium mb-6 text-center" data-id="element-58">
                Un libro digital fácil de seguir donde aprenderás cómo preparar
                pomadas naturales en casa usando plantas medicinales.
              </p>

              <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-xl font-bold text-green-700" data-id="element-59">
                <span data-id="element-60">✓ Sin químicos</span>
                <span data-id="element-61">✓ Sin complicaciones</span>
                <span data-id="element-62">✓ Solo ingredientes naturales</span>
              </div>
            </div>

            <p className="text-center text-xl md:text-2xl font-bold text-green-700" data-id="element-63">
              Y muchas de esas recetas han sobrevivido durante siglos.
            </p>
          </div>
        </section>

        {/* SECTION 3 - WHAT YOU'LL DISCOVER */}
        <section className="bg-[#1a2e1a] text-white py-20 px-4" data-id="element-64">
          <div className="max-w-4xl mx-auto text-center" data-id="element-65">
            <h2 className="text-4xl md:text-5xl font-black mb-12 text-green-400" data-id="element-66">
              🌱 Dentro del Libro Descubrirás
            </h2>

            <ul className="text-lg md:text-xl space-y-4 text-left max-w-2xl mx-auto bg-[#243524] p-8 rounded-xl border border-green-900" data-id="element-67">
              <li className="flex items-center gap-3" data-id="element-68">
                <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" data-id="element-69"></div>{' '}
                Cómo preparar pomadas medicinales paso a paso
              </li>
              <li className="flex items-center gap-3" data-id="element-70">
                <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" data-id="element-71"></div>{' '}
                Qué plantas usar para cada problema
              </li>
              <li className="flex items-center gap-3" data-id="element-72">
                <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" data-id="element-73"></div>{' '}
                Cómo combinar aceites naturales
              </li>
              <li className="flex items-center gap-3" data-id="element-74">
                <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" data-id="element-75"></div>{' '}
                Cómo conservar tus preparaciones
              </li>
              <li className="flex items-center gap-3" data-id="element-76">
                <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" data-id="element-77"></div>{' '}
                Cómo aprovechar propiedades curativas de las plantas
              </li>
            </ul>

            <blockquote className="text-2xl md:text-3xl text-green-300 italic mt-10 font-semibold" data-id="element-78">
              "Todo explicado de forma simple para que cualquier persona pueda
              hacerlo en casa."
            </blockquote>
          </div>
        </section>

        {/* SECTION 4 - SPECIFIC POMADAS */}
        <section className="py-20 px-4 bg-white" data-id="element-79">
          <div className="max-w-5xl mx-auto text-center" data-id="element-80">
            <h2 className="text-3xl md:text-4xl font-black mb-12" data-id="element-81">
              Algunas Pomadas Que Aprenderás a Preparar
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-id="element-82">
              <div className="bg-green-50 p-6 rounded-xl border border-green-200" data-id="element-83">
                <p className="text-lg font-bold text-green-800" data-id="element-84">
                  🌿 Pomada de Árnica
                </p>
                <p className="text-gray-600" data-id="element-85">para golpes y hematomas</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200" data-id="element-86">
                <p className="text-lg font-bold text-green-800" data-id="element-87">
                  🌿 Pomada de Caléndula
                </p>
                <p className="text-gray-600" data-id="element-88">para la piel</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200" data-id="element-89">
                <p className="text-lg font-bold text-green-800" data-id="element-90">
                  🌿 Pomada de Romero
                </p>
                <p className="text-gray-600" data-id="element-91">para dolores musculares</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200" data-id="element-92">
                <p className="text-lg font-bold text-green-800" data-id="element-93">
                  🌿 Pomada de Lavanda
                </p>
                <p className="text-gray-600" data-id="element-94">para picaduras</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200" data-id="element-95">
                <p className="text-lg font-bold text-green-800" data-id="element-96">
                  🌿 Pomada de Eucalipto
                </p>
                <p className="text-gray-600" data-id="element-97">para congestión</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200" data-id="element-98">
                <p className="text-lg font-bold text-green-800" data-id="element-99">
                  🌿 Pomada de Sábila
                </p>
                <p className="text-gray-600" data-id="element-100">para quemaduras</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl border border-green-200 md:col-start-2" data-id="element-101">
                <p className="text-lg font-bold text-green-800" data-id="element-102">
                  🌿 Pomada de Consuelda
                </p>
                <p className="text-gray-600" data-id="element-103">para articulaciones</p>
              </div>
            </div>

            <p className="text-2xl font-bold text-green-700 mb-10" data-id="element-104">
              Y muchas más… hasta completar 50 recetas naturales.
            </p>

            <CTAButton text="🌿 QUIERO APRENDER LAS 50 RECETAS AHORA" href="#pack-completo" data-id="element-105" />
          </div>
        </section>

        {/* SECTION 5 - BONUSES */}
        <section className="py-20 px-4 bg-[#1a2e1a] text-white" data-id="element-106">
          <div className="max-w-5xl mx-auto" data-id="element-107">
            <div className="text-center mb-12" data-id="element-108">
              <h2 className="text-4xl md:text-5xl font-black mb-4 text-green-400" data-id="element-109">
                🎁 Pero Eso No Es Todo… Bonos Especiales GRATIS
              </h2>

              <div className="inline-block bg-red-600 text-white font-black px-6 py-2 rounded-lg text-xl animate-pulse" data-id="element-110">
                Estos bonos son GRATIS por el día de hoy
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-111">
              {/* Bono 1 - lazy loaded with explicit dimensions */}
              <div className="bg-white text-gray-900 rounded-2xl overflow-hidden shadow-2xl flex flex-col" data-id="element-112">
                <a href="#compra" className="block overflow-hidden bg-gray-100" data-id="element-113">
                  <div style={{
                  aspectRatio: '2/3'
                }} data-id="element-114">
                    <img src="https://cdn.magicpatterns.com/uploads/4gaUUyuxDSMWH8dinV5SJb/ChatGPT_Image_8_mar_2026,_23_43_03.png" alt="100 Plantas Medicinales y Sus Beneficios" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" width={1024} height={1536} loading="lazy" decoding="async" data-id="element-115" />
                  </div>
                </a>
                <div className="p-6 flex-1 flex flex-col" data-id="element-116">
                  <h3 className="text-xl font-black text-green-700 mb-4" data-id="element-117">
                    🎁 BONO #1 – 100 Plantas Medicinales y Sus Beneficios
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-118">
                    Una guía práctica donde descubrirás 100 plantas medicinales
                    y sus propiedades.
                  </p>
                  <ul className="space-y-3 font-medium flex-1" data-id="element-119">
                    <li className="flex items-start gap-2" data-id="element-120">
                      <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-121" />{' '}
                      Descubre 100 plantas medicinales
                    </li>
                    <li className="flex items-start gap-2" data-id="element-122">
                      <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-123" />{' '}
                      Propiedades de cada planta
                    </li>
                    <li className="flex items-start gap-2" data-id="element-124">
                      <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-125" />{' '}
                      Cómo utilizarlas correctamente
                    </li>
                  </ul>
                </div>
              </div>

              {/* Bono 2 - lazy loaded with explicit dimensions */}
              <div className="bg-white text-gray-900 rounded-2xl overflow-hidden shadow-2xl flex flex-col" data-id="element-126">
                <a href="#compra" className="block overflow-hidden bg-gray-100" data-id="element-127">
                  <div style={{
                  aspectRatio: '2/3'
                }} data-id="element-128">
                    <img src="https://cdn.magicpatterns.com/uploads/oZMDpok9SdzbziXJW8mBes/ChatGPT_Image_8_mar_2026,_23_42_46.png" alt="Guía de Aceites Medicinales Naturales" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" width={1024} height={1536} loading="lazy" decoding="async" data-id="element-129" />
                  </div>
                </a>
                <div className="p-6 flex-1 flex flex-col" data-id="element-130">
                  <h3 className="text-xl font-black text-green-700 mb-4" data-id="element-131">
                    🎁 BONO #2 – Guía de Aceites Medicinales Naturales
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-132">
                    Los aceites son la base de muchas pomadas. En esta guía
                    aprenderás todo sobre ellos.
                  </p>
                  <ul className="space-y-3 font-medium flex-1" data-id="element-133">
                    <li className="flex items-start gap-2" data-id="element-134">
                      <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-135" />{' '}
                      Qué aceites utilizar
                    </li>
                    <li className="flex items-start gap-2" data-id="element-136">
                      <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-137" />{' '}
                      Propiedades de cada aceite
                    </li>
                    <li className="flex items-start gap-2" data-id="element-138">
                      <CheckCircle2 className="text-green-600 w-5 h-5 flex-shrink-0" data-id="element-139" />{' '}
                      Cómo preparar aceites medicinales
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 - WHAT YOU'LL RECEIVE */}
        <section className="py-16 px-4 bg-white" data-id="element-140">
          <div className="max-w-4xl mx-auto text-center" data-id="element-141">
            <h2 className="text-3xl md:text-4xl font-black mb-10" data-id="element-142">
              📦 En Total Recibirás
            </h2>

            <div className="space-y-4 max-w-xl mx-auto mb-8" data-id="element-143">
              <div className="flex items-center gap-4 bg-green-50 p-4 rounded-xl border border-green-200" data-id="element-144">
                <CheckCircle2 className="text-green-600 w-8 h-8 flex-shrink-0" data-id="element-145" />
                <p className="text-lg font-bold text-left" data-id="element-146">
                  Libro principal: Las 50 Pomadas Medicinales
                </p>
              </div>
              <div className="flex items-center gap-4 bg-green-50 p-4 rounded-xl border border-green-200" data-id="element-147">
                <CheckCircle2 className="text-green-600 w-8 h-8 flex-shrink-0" data-id="element-148" />
                <p className="text-lg font-bold text-left" data-id="element-149">
                  Bono 1: 100 Plantas Medicinales
                </p>
              </div>
              <div className="flex items-center gap-4 bg-green-50 p-4 rounded-xl border border-green-200" data-id="element-150">
                <CheckCircle2 className="text-green-600 w-8 h-8 flex-shrink-0" data-id="element-151" />
                <p className="text-lg font-bold text-left" data-id="element-152">
                  Bono 2: Guía de Aceites Medicinales
                </p>
              </div>
            </div>

            <p className="text-xl font-semibold text-gray-600" data-id="element-153">
              Todo en formato digital listo para descargar.
            </p>
          </div>
        </section>

        {/* SECTION 7 - WHO IS THIS FOR */}
        <section className="py-16 px-4 bg-gray-50" data-id="element-154">
          <div className="max-w-4xl mx-auto" data-id="element-155">
            <div className="bg-white p-8 rounded-2xl shadow-md border-t-4 border-green-600" data-id="element-156">
              <h2 className="text-2xl font-black mb-6 text-gray-900" data-id="element-157">
                💚 ¿Para Quién Es Este Libro?
              </h2>
              <p className="text-lg text-gray-600 mb-6" data-id="element-158">
                Este libro es ideal si:
              </p>
              <ul className="space-y-4 font-medium text-lg" data-id="element-159">
                <li className="flex items-start gap-3" data-id="element-160">
                  <CheckCircle2 className="text-green-600 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-161" />{' '}
                  Te interesa la medicina natural
                </li>
                <li className="flex items-start gap-3" data-id="element-162">
                  <CheckCircle2 className="text-green-600 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-163" />{' '}
                  Quieres aprender remedios tradicionales
                </li>
                <li className="flex items-start gap-3" data-id="element-164">
                  <CheckCircle2 className="text-green-600 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-165" />{' '}
                  Te gustan las plantas medicinales
                </li>
                <li className="flex items-start gap-3" data-id="element-166">
                  <CheckCircle2 className="text-green-600 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-167" />{' '}
                  Buscas alternativas naturales para el cuidado de la piel
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 8 - IMAGINE */}
        <section className="py-20 px-4 bg-white" data-id="element-168">
          <div className="max-w-4xl mx-auto text-center" data-id="element-169">
            <h2 className="text-4xl md:text-5xl font-black mb-12" data-id="element-170">
              ⚡ Imagina Poder…
            </h2>

            <div className="space-y-6 text-2xl md:text-3xl font-bold text-green-700" data-id="element-171">
              <p data-id="element-172">🌿 Preparar tus propias pomadas naturales</p>
              <p data-id="element-173">
                🌿 Conocer plantas medicinales que muchas personas han olvidado
              </p>
              <p data-id="element-174">
                🌿 Recuperar conocimientos que se usaban antes de las farmacias
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 9 - TESTIMONIALS - all avatar images lazy loaded */}
        <section className="py-20 px-4 bg-gray-50" data-id="element-175">
          <div className="max-w-6xl mx-auto" data-id="element-176">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-12" data-id="element-177">
              Lo que dicen quienes ya tienen el libro...
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-178">
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100" data-id="element-179">
                <div className="flex items-center gap-4 mb-4" data-id="element-180">
                  <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" aria-hidden="true" data-id="element-181">
                    CL
                  </div>
                  <div data-id="element-182">
                    <h4 className="font-bold text-gray-900" data-id="element-183">Carmen López</h4>
                    <p className="text-sm text-gray-600" data-id="element-184">
                      Ama de casa - Córdoba, Argentina
                    </p>
                  </div>
                </div>
                <div className="flex text-green-700 mb-2" data-id="element-185">★★★★★</div>
                <p className="text-gray-700 italic" data-id="element-186">
                  "Preparé la pomada de árnica y me ayudó muchísimo con un golpe
                  que se dio mi hijo. Nunca pensé que fuera tan fácil de hacer."
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100" data-id="element-187">
                <div className="flex items-center gap-4 mb-4" data-id="element-188">
                  <div className="w-14 h-14 rounded-full bg-emerald-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" aria-hidden="true" data-id="element-189">
                    RM
                  </div>
                  <div data-id="element-190">
                    <h4 className="font-bold text-gray-900" data-id="element-191">Rosa Martínez</h4>
                    <p className="text-sm text-gray-600" data-id="element-192">
                      Terapeuta natural - Rosario, Argentina
                    </p>
                  </div>
                </div>
                <div className="flex text-green-700 mb-2" data-id="element-193">★★★★★</div>
                <p className="text-gray-700 italic" data-id="element-194">
                  "Las recetas son fáciles de seguir. Ya hice 5 pomadas
                  diferentes y mis clientes están encantados con los
                  resultados."
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100" data-id="element-195">
                <div className="flex items-center gap-4 mb-4" data-id="element-196">
                  <div className="w-14 h-14 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" aria-hidden="true" data-id="element-197">
                    EG
                  </div>
                  <div data-id="element-198">
                    <h4 className="font-bold text-gray-900" data-id="element-199">Elena García</h4>
                    <p className="text-sm text-gray-600" data-id="element-200">
                      Herbolaria - Mendoza, Argentina
                    </p>
                  </div>
                </div>
                <div className="flex text-green-700 mb-2" data-id="element-201">★★★★★</div>
                <p className="text-gray-700 italic" data-id="element-202">
                  "Mi abuela usaba remedios así. Qué alegría recuperar ese
                  conocimiento y poder transmitirlo a mis hijas."
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 lg:col-start-1 lg:col-end-2 lg:ml-auto lg:w-full" data-id="element-203">
                <div className="flex items-center gap-4 mb-4" data-id="element-204">
                  <div className="w-14 h-14 rounded-full bg-green-800 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" aria-hidden="true" data-id="element-205">
                    PS
                  </div>
                  <div data-id="element-206">
                    <h4 className="font-bold text-gray-900" data-id="element-207">
                      Patricia Sánchez
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-208">
                      Madre de familia - Tucumán, Argentina
                    </p>
                  </div>
                </div>
                <div className="flex text-green-700 mb-2" data-id="element-209">★★★★★</div>
                <p className="text-gray-700 italic" data-id="element-210">
                  "La guía de aceites es increíble. Ahora entiendo qué aceite
                  usar para cada cosa. Muy completo todo."
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 lg:col-start-2 lg:col-end-3" data-id="element-211">
                <div className="flex items-center gap-4 mb-4" data-id="element-212">
                  <div className="w-14 h-14 rounded-full bg-lime-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" aria-hidden="true" data-id="element-213">
                    LF
                  </div>
                  <div data-id="element-214">
                    <h4 className="font-bold text-gray-900" data-id="element-215">Lucía Fernández</h4>
                    <p className="text-sm text-gray-600" data-id="element-216">
                      Emprendedora - Mar del Plata, Argentina
                    </p>
                  </div>
                </div>
                <div className="flex text-green-700 mb-2" data-id="element-217">★★★★★</div>
                <p className="text-gray-700 italic" data-id="element-218">
                  "Nunca pensé que preparar pomadas fuera tan sencillo. El libro
                  lo explica todo paso a paso. ¡Muy recomendado!"
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 10 - UPSELL + PURCHASE */}
        <UpsellSection data-id="element-219" />

        {/* SECTION 11 - INSTANT ACCESS */}
        <section className="py-20 px-4 bg-[#1a2e1a] text-white text-center" data-id="element-220">
          <div className="max-w-4xl mx-auto" data-id="element-221">
            <h2 className="text-4xl md:text-5xl font-black mb-8 text-green-400" data-id="element-222">
              💻 Acceso Inmediato
            </h2>
            <p className="text-xl md:text-2xl mb-8" data-id="element-223">
              Una vez que completes tu compra recibirás acceso inmediato al
              libro digital para descargarlo y comenzar a usarlo hoy mismo.
            </p>

            <p className="text-lg mb-6" data-id="element-224">Podrás verlo desde:</p>

            <div className="flex flex-col md:flex-row justify-center items-center gap-8 text-xl font-bold" data-id="element-225">
              <span className="flex items-center gap-2" data-id="element-226">
                <Smartphone className="w-8 h-8 text-green-400" data-id="element-227" /> Celular
              </span>
              <span className="flex items-center gap-2" data-id="element-228">
                <Monitor className="w-8 h-8 text-green-400" data-id="element-229" /> Computadora
              </span>
              <span className="flex items-center gap-2" data-id="element-230">
                <Tablet className="w-8 h-8 text-green-400" data-id="element-231" /> Tablet
              </span>
            </div>
          </div>
        </section>

        {/* SECTION 12 - FINAL CTA */}
        <section className="py-24 px-4 bg-white text-center" data-id="element-232"></section>
      </main>
    </div>;
}